import React from 'react';
import { useTable, useSortBy } from 'react-table';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowRightArrowLeft } from 'react-icons/fa6';
import { HiPuzzle } from 'react-icons/hi';
import { ImSpoonKnife } from 'react-icons/im';
import { Tooltip } from '../../common/Tooltip.js';
import { buildOnCallPupilSummaryRows } from '../OnCallFunc.js';


export function PupilGroupIcons({ pupilRecord }) {
    const groupIcons = [
        {
            key: 'FSM',
            label: 'FSM',
            show: pupilRecord?.FSM,
            icon: <ImSpoonKnife aria-hidden='true' />,
        },
        {
            key: 'ALN',
            label: 'ALN',
            show: pupilRecord?.ALN,
            icon: <HiPuzzle aria-hidden='true' />,
        },
        {
            key: 'MTT',
            label: 'MTT',
            show: pupilRecord?.MTT,
            icon: <FaArrowRightArrowLeft aria-hidden='true' />,
        },
    ].filter((groupIcon) => groupIcon.show);

    if (groupIcons.length === 0) {
        return <span className='oncall_reports_pupilGroupsEmpty' aria-label='No groups' />;
    }

    return (
        <div className='oncall_reports_pupilGroupsCell'>
            {groupIcons.map((groupIcon) => (
                <Tooltip
                    key={groupIcon.key}
                    text={groupIcon.label}
                    containerClass='oncall_reports_pupilGroupTooltip'
                >
                    <span
                        className={`oncall_reports_pupilGroupIcon oncall_reports_pupilGroupIcon_${groupIcon.key.toLowerCase()}`}
                        aria-label={groupIcon.label}
                    >
                        {groupIcon.icon}
                    </span>
                </Tooltip>
            ))}
        </div>
    );
}


export function PupilCallTypeSummaryTable({ pupilRecords, callTypes }) {

    const navigate = useNavigate();  // Hook for navigation
    const location = useLocation();

    const columns = React.useMemo(() => [
        {
            Header: "Learner's Name",
            accessor: 'pupilName'  // Define how to get the cell's value
        },
        {
            Header: 'Groups',
            id: 'groups',
            accessor: (row) => [row.FSM, row.ALN, row.MTT].filter(Boolean).length,
            Cell: ({ row }) => <PupilGroupIcons pupilRecord={row.original} />,
            disableSortBy: true,
        },
        ...callTypes.map(type => ({
            Header: type.typeName,
            accessor: d => d.totals[type.typeName],
            sortType: 'basic'
        })),
        {
            Header: 'Total Calls',
            accessor: 'totals.total',
            sortType: 'basic'
        }
    ], [callTypes]);

    const data = React.useMemo(() => buildOnCallPupilSummaryRows(pupilRecords, callTypes), [pupilRecords, callTypes]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable({ columns, data }, useSortBy);

    // Render the UI for your table
    return (
        <table {...getTableProps()} className="summary-table">
            <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                {column.render('Header')}
                                <span>
                                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                </span>
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()}>
                {rows.map(row => {
                    prepareRow(row)
                    return (
                        <tr {...row.getRowProps({
                            onClick: () => navigate(
                                `/app/oncall/reports/individual/${row.original.id}${location.search}`,
                                { state: { backPath: `${location.pathname}${location.search}` } }
                            )
                        })}>
                            {row.cells.map(cell => (
                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
